import { observer } from 'owa-mobx-react';
import {
    SelectAllOnFilled,
    SelectAllOnRegular,
    SelectAllOffFilled,
    SelectAllOffRegular,
    bundleIcon,
} from '@fluentui/react-icons';
import { Button, Tooltip } from '@fluentui/react-components';
import toggleSelectAllState from '../mutators/toggleSelectAllState';
import resetSelectAllState from '../mutators/resetSelectAllState';
import tableExitVirtualSelectAllMode from 'owa-mail-list-selection-actions-v2/lib/internal/mutators/tableExitVirtualSelectAllMode';
import setTableIsInCheckedMode from 'owa-mail-list-selection-actions-v2/lib/internal/mutators/setTableIsInCheckedMode';
import { getDensityModeCssClass, getDensityModeString } from 'owa-fabric-theme';
import loc from 'owa-localize';
import { selectLabel } from 'owa-locstrings/lib/strings/selectlabel.locstring.json';
import { resetSelection } from 'owa-mail-actions/lib/mailListSelectionActions';
import MailListItemSelectionSource from 'owa-mail-store/lib/store/schema/MailListItemSelectionSource';
import React from 'react';
import {
    selectAllIconFull,
    selectAllIconMediumAndCompact,
    full,
    medium,
    compact,
    selectAllCheckboxButton,
    selectAllCheckboxButtonMedium,
    selectAllCheckboxButtonCompact,
    buttonChecked,
} from './MailListHeader.scss';

import { useIconFillOnHover } from 'owa-mail-layout';
import classnames from 'owa-classnames';
import { useIsTypeRampUpdatesEnabled } from 'owa-mail-list-item-shared/lib/utils/useIsTypeRampUpdatesEnabled';

const SelectAllOnIcon = bundleIcon(SelectAllOnFilled, SelectAllOnRegular);
const SelectAllOffIcon = bundleIcon(SelectAllOffFilled, SelectAllOffRegular);

export interface MailListSelectAllProps {
    tableView: any;
}

const defaultExport: React.FunctionComponent<MailListSelectAllProps> = observer(
    function MailListSelectAll(props: MailListSelectAllProps) {
        const { tableView } = props;
        const isFullDensity = getDensityModeString() === 'full';
        const isRPDenselessEnabled = useIsTypeRampUpdatesEnabled();
        const { filled, iconColorClassName, onMouseEnter, onMouseLeave } = useIconFillOnHover();

        const isChecked = tableView.isInSelectAllState || tableView.isInCheckedMode;
        const iconClassName = classnames(
            isFullDensity || isRPDenselessEnabled
                ? selectAllIconFull
                : selectAllIconMediumAndCompact,
            iconColorClassName
        );
        const selectAllIcon = React.useMemo(() => {
            const IconComponent = isChecked ? SelectAllOnIcon : SelectAllOffIcon;
            return <IconComponent filled={filled} className={iconClassName} />;
        }, [isChecked, filled, iconClassName]);

        const onCheckboxClick = React.useCallback(() => {
            // Overrides select checkbox on left of mail list header or single mail item checkbox checked - removes from Multi Select state when unchecked
            if (tableView.isInVirtualSelectAllMode || tableView.isInCheckedMode) {
                tableExitVirtualSelectAllMode(tableView);
                setTableIsInCheckedMode(tableView, false);
                resetSelectAllState(tableView);
                resetSelection(tableView, MailListItemSelectionSource.Reset);
            } else {
                toggleSelectAllState(tableView);
            }
        }, [tableView]);

        React.useEffect(() => {
            resetSelectAllState(tableView);
        }, [tableView]);

        React.useEffect(() => {
            if (!tableView.isInCheckedMode) {
                resetSelectAllState(tableView);
            }
        }, [tableView.isInCheckedMode]);

        const onCheckboxKeyDown = React.useCallback(
            (event: React.KeyboardEvent<unknown>) => {
                if (event.key === 'Escape') {
                    resetSelectAllState(tableView);
                }
            },
            [tableView]
        );

        return (
            <div className={classnames(getDensityModeCssClass(full, medium, compact))}>
                <Tooltip content={loc(selectLabel)} relationship="label" withArrow={true}>
                    <span>
                        <Button
                            className={classnames(
                                selectAllCheckboxButton,
                                (tableView.isInSelectAllState || tableView.isInCheckedMode) &&
                                    buttonChecked,
                                isRPDenselessEnabled
                                    ? selectAllCheckboxButtonMedium
                                    : getDensityModeCssClass(
                                          undefined,
                                          selectAllCheckboxButtonMedium,
                                          selectAllCheckboxButtonCompact
                                      )
                            )}
                            icon={selectAllIcon}
                            onClick={onCheckboxClick}
                            onKeyDown={onCheckboxKeyDown}
                            onMouseEnter={onMouseEnter}
                            onMouseLeave={onMouseLeave}
                            aria-label={loc(selectLabel)}
                        ></Button>
                    </span>
                </Tooltip>
            </div>
        );
    },
    'MailListSelectAll'
);
export default defaultExport;
